import * as z from "zod";
import { anyOfFilters, pagination, version } from "../index";
import {
  aggregateSyncDto,
  type RetryWaitingInput,
  retryWaitingDto,
  retryWaitingInput,
  syncStatuses,
  waitingItemCodes,
  waitingSources,
} from "../til";
import { adjustmentDirections, differenceWaitingReasons, movementStatuses } from "./enums";

// The adjustments of stock the IMS proposes (FR-M12-01, FR-M12-02, B-54 point 1, ADR-226; 50-api/logistics.md): a count
// of the warehouse that does not match the logical stock (I-11 `kind: difference`) becomes a proposed adjustment, a
// `logistics.stock_movement` of type `inventory_adjustment`, with the account and the memo prefilled. Logistics
// confirms it or dismisses it; beyond a threshold Finance approves it too (gate `logistics.stockAdjustment`). Confirmed,
// it goes to Business Central alone (I-25 `inventory_adjustment`).

/** Logistics confirms or dismisses a proposed adjustment, sends it to BC again and applies again a count that waits. */
export const ADJUSTMENT_CONFIRM = "adjustment.confirm";
/** Finance approves or refuses an adjustment beyond the threshold, with the role of the gate. */
export const ADJUSTMENT_APPROVE = "adjustment.approve";

/** The account of BC an adjustment is posted to, in the code of BC (e.g. `50012`). */
export const glAccount = z.string().trim().min(1).max(20);

const reason = z.string().trim().min(1).max(2000);

export const getStockAdjustmentInput = z.object({ adjustmentId: z.uuid() });
export type GetStockAdjustmentInput = z.input<typeof getStockAdjustmentInput>;
/** An adjustment by its id: the path of the routes of one adjustment. */
export const stockAdjustmentParams = getStockAdjustmentInput.pick({ adjustmentId: true });

/** An action on an adjustment: `version` is the one the client read (409 otherwise). */
export const stockAdjustmentActionInput = getStockAdjustmentInput.extend({ version });
export type StockAdjustmentActionInput = z.input<typeof stockAdjustmentActionInput>;
export const stockAdjustmentActionBody = stockAdjustmentActionInput.omit({ adjustmentId: true });

/** Logistics dismisses a proposed adjustment, or Finance refuses one: always with the reason. */
export const stockAdjustmentReasonInput = stockAdjustmentActionInput.extend({ reason });
export type StockAdjustmentReasonInput = z.input<typeof stockAdjustmentReasonInput>;
export const stockAdjustmentReasonBody = stockAdjustmentReasonInput.omit({ adjustmentId: true });

/**
 * Logistics confirms a proposed adjustment (FR-M12-02), with the account and the memo it was proposed with, or with
 * the ones it gives here. An adjustment without an account cannot be confirmed.
 */
export const confirmStockAdjustmentInput = stockAdjustmentActionInput.extend({
  glAccount: glAccount.optional(),
  memo: z.string().trim().min(1).max(4000).optional(),
});
export type ConfirmStockAdjustmentInput = z.input<typeof confirmStockAdjustmentInput>;
export const confirmStockAdjustmentBody = confirmStockAdjustmentInput.omit({ adjustmentId: true });

const listFilters = {
  status: z.enum(movementStatuses).optional(),
  /** The warehouse the adjustment is about. */
  location: z.uuid().optional(),
};

/** The adjustments in at least one of these sets of the filters (ADR-302): the queues of the adjustments, to do. */
const anyOfAdjustments = { anyOf: anyOfFilters(listFilters).optional() };

/** The adjustments, the latest proposed first. */
export const listStockAdjustmentsInput = z.object({
  ...listFilters,
  ...anyOfAdjustments,
  /** Part of the number of the adjustment or of the count of the warehouse, case-insensitive, without separators. */
  q: z.string().trim().min(1).max(80).optional(),
  ...pagination({ defaultSize: 50, maxSize: 200 }),
});
export type ListStockAdjustmentsInput = z.input<typeof listStockAdjustmentsInput>;

/** How many adjustments with the same filters, and since when the oldest was proposed (ADR-086): a queue of the home. */
export const countStockAdjustmentsInput = z.object({ ...listFilters, ...anyOfAdjustments });
export type CountStockAdjustmentsInput = z.input<typeof countStockAdjustmentsInput>;

/** An adjustment as its list shows it. */
export const stockAdjustmentSummaryDto = z
  .object({
    id: z.uuid(),
    /** Generated by Nexxt (`RA-`, the warehouse, the day and a sequence): how BC names it. */
    adjustmentNumber: z.string(),
    /** `draft`: proposed, waiting for Logistics; `awaiting_confirmation`: waiting for Finance; then `confirmed`. */
    status: z.enum(movementStatuses),
    location: z.object({
      id: z.uuid(),
      code: z.string(),
      name: z.string(),
      /** `reference.legal_entity.code`: the company the adjustment is posted in. */
      company: z.string(),
    }),
    /** The account of BC, prefilled from the company of the warehouse; null until someone gives it. */
    glAccount: z.string().nullable(),
    memo: z.string().nullable(),
    /** The count of the warehouse the adjustment answers (I-11 `differenceRef`). */
    differenceRef: z.string().nullable(),
    /** When the warehouse counted. */
    countedAt: z.iso.datetime().nullable(),
    /** The number of the document Business Central registered, from the outcome of I-25. */
    bcDocumentNo: z.string().nullable(),
    proposedAt: z.iso.datetime().nullable(),
    confirmedAt: z.iso.datetime().nullable(),
    /** Why it was dismissed by Logistics or refused by Finance. */
    cancelReason: z.string().nullable(),
    /** How Business Central (I-25) took its last state. */
    bcSyncStatus: z.enum(syncStatuses),
    /** Units the warehouse counted more than the IMS had, and fewer: what the adjustment adds and takes. */
    unitsIncrease: z.number().int(),
    unitsDecrease: z.number().int(),
    lineCount: z.number().int(),
    version: z.number().int(),
  })
  .meta({ id: "StockAdjustmentSummary" });
export type StockAdjustmentSummaryDto = z.output<typeof stockAdjustmentSummaryDto>;

/** An adjustment with its lines: alone and after an action. */
export const stockAdjustmentDto = stockAdjustmentSummaryDto
  .extend({
    confirmedBy: z.uuid().nullable(),
    lines: z.array(
      z.object({
        id: z.uuid(),
        sku: z.object({ id: z.uuid(), skuCode: z.string().nullable(), ean: z.string().nullable() }),
        /** The units the IMS had, and the ones the warehouse counted. */
        logicalQty: z.number().int(),
        countedQty: z.number().int(),
        /** The units the adjustment adds or takes: the difference between the two. */
        qty: z.number().int(),
        direction: z.enum(adjustmentDirections),
      }),
    ),
  })
  .meta({ id: "StockAdjustment" });
export type StockAdjustmentDto = z.output<typeof stockAdjustmentDto>;

/** How an adjustment stands towards Business Central (I-25): the last state, what BC refused, and if it can go again. */
export const stockAdjustmentSyncDto = aggregateSyncDto
  .extend({ adjustmentId: z.uuid() })
  .meta({ id: "StockAdjustmentSync" });
export type StockAdjustmentSyncDto = z.output<typeof stockAdjustmentSyncDto>;

// --- Counts of the warehouse Nexxt could not make an adjustment of (I-11 §5) ---

const waitingFilters = { reason: z.enum(differenceWaitingReasons).optional() };

/** The counts of the warehouse that wait, the latest first: each lists at most 50 of its items (`itemCount`). */
export const listWaitingDifferencesInput = z.object({
  ...waitingFilters,
  ...pagination({ defaultSize: 50, maxSize: 100 }),
});
export type ListWaitingDifferencesInput = z.input<typeof listWaitingDifferencesInput>;

/** How many counts wait with the same filters, and since when the oldest (ADR-086): a queue of the home. */
export const countWaitingDifferencesInput = z.object(waitingFilters);
export type CountWaitingDifferencesInput = z.input<typeof countWaitingDifferencesInput>;

/** Applies again the counts that wait, once the mapping or the SKU is set right. */
export const retryWaitingDifferencesInput = retryWaitingInput;
export type RetryWaitingDifferencesInput = RetryWaitingInput;
export const retryWaitingDifferencesDto = retryWaitingDto("WaitingDifferencesRetried");

/** An item the reason of a count that waits is about, with its codes as the warehouse sent them. */
export const waitingDifferenceItem = z.object(waitingItemCodes);
export type WaitingDifferenceItem = z.output<typeof waitingDifferenceItem>;

/** A count of the warehouse that waits, whole: no adjustment is proposed until all of it resolves. */
export const waitingDifferenceDto = z
  .object({
    id: z.uuid(),
    /** The number of the count in the system of the warehouse. */
    differenceRef: z.string(),
    /** The system the fact came from (ADR-144): the warehouse and the items are in its codes. */
    source: z.enum(waitingSources),
    /** The warehouse in the codes of `source`. */
    locationCode: z.string(),
    countedAt: z.iso.datetime(),
    reason: z.enum(differenceWaitingReasons),
    /** The first of the items the reason is about: none for the count as a whole. */
    items: z.array(waitingDifferenceItem),
    /** How many items the reason is about. */
    itemCount: z.number().int(),
    /** Since when it waits. */
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: "WaitingDifference" });
export type WaitingDifferenceDto = z.output<typeof waitingDifferenceDto>;
